// TypeScript mirrors of surveyshield/models/instrument_review.py
// (and the shared schemas in surveyshield/models/_shared.py).
//
// v0.2.x JSON shape:
//   - `dimensions` → `categories` (8-category bot-resistance rubric)
//   - `Finding.question_id` → `Finding.locator`
//   - `contributing_dimensions: string[]` → `contributing_categories: Category[]`
//   - `related_dimensions: string[]` → `related_categories: Category[]`

export type Severity = 1 | 2 | 3;

export type EditKind =
  | "none"
  | "add_attention_check"
  | "add_identity_question"
  | "add_impossible_event"
  | "add_visual_trap"
  | "add_open_end_probe"
  | "add_behavioral_telemetry"
  | "add_context_probe"
  | "add_eclair_probe"
  | "enable_force_response"
  | "add_display_logic"
  | "remove_question"
  | "edit_question_text"
  | "reorder_or_randomize"
  | "other";

// Mirrors surveyshield.categories.Category. Keep in sync with the
// 8-category registry in surveyshield/categories.py.
export type Category =
  | "logical"
  | "visual"
  | "content-traps"
  | "open-ends"
  | "mouse-keyboard"
  | "behavioral"
  | "local-awareness"
  | "eclaire";

export const CATEGORY_DISPLAY_NAMES: Record<Category, string> = {
  "logical": "Logic",
  "visual": "Visual Reasoning",
  "content-traps": "Traps",
  "open-ends": "Open Ends",
  "mouse-keyboard": "Mouse and Keyboard Input",
  "behavioral": "Behavioral",
  "local-awareness": "Context Awareness",
  "eclaire": "ECLAIR",
};

export interface Finding {
  // Either a static-review QID ("QID42") or a live-runtime step locator
  // ("step:17"). Null means a survey-/run-level finding.
  locator?: string | null;
  excerpt: string;
  severity: Severity;
  confidence: number;
  rationale: string;
  suggested_fix?: string | null;
  recommended_edit_kind: EditKind;
  contributing_categories: Category[];
}

export interface OverallFeedback {
  headline: string;
  paragraphs: string[];
}

export interface CategoryReview {
  category: Category;
  score: number;
  findings: Finding[];
  summary: string;
  model: string;
  duration_seconds: number;
  error?: string | null;
}

export interface Recommendation {
  title: string;
  detail: string;
  priority: number;
  related_categories: Category[];
}

export interface InstrumentReview {
  review_id: string;
  filename: string;
  timestamp: string;
  overall_score: number;
  completion_likelihood: number;
  parsed_summary: Record<string, unknown>;
  categories: CategoryReview[];
  recommendations: Recommendation[];
  overall_feedback: OverallFeedback;
  narrative_summary: string;
  methods_statement: string;
  parameters: Record<string, unknown>;
}

export interface InstrumentReviewSubmitResponse {
  review_id: string;
  status: string;
  message: string;
  estimated_time: string;
}

export interface InstrumentReviewStatus {
  review_id: string;
  status: "queued" | "running" | "completed" | "failed";
  message: string;
  ready?: boolean;
}
